import { Injectable } from '@angular/core';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { catchError, tap } from 'rxjs/operators';
import { BehaviorSubject, Subject, throwError } from 'rxjs';
import { environment } from '../../../environments/environment';
import { Router } from '@angular/router';
import { FormGroup } from '@angular/forms';
import { Department } from '../../shared/models/department.model';
import { LogService } from 'src/app/shared/services/log.service';

@Injectable({ providedIn: 'root' })
export class DepartmentService {
  apiHost: string = environment.apiHost;
  departmentArr: Department[] = [];
  constructor(
    private http: HttpClient,
    private router: Router,
    private logService: LogService
  ) {}

  async loadDepartments() {
    this.departmentArr = await this.getAllDepartments();
    if (this.departmentArr == null) this.departmentArr = [];
    this.departmentArr = this.departmentArr.sort((a, b) => {
      if (a.name < b.name) return -1;
      if (a.name > b.name) return 1;
      return 0;
    });
  }

  async loadActiveDepartments() {
    this.departmentArr = await this.getActiveDepartments();
    if (this.departmentArr == null) this.departmentArr = [];
    this.departmentArr = this.departmentArr.sort((a, b) => {
      if (a.name < b.name) return -1;
      if (a.name > b.name) return 1;
      return 0;
    });
  }  

  getActiveDepartments(): Promise<Department[]> {
    let promise = new Promise<Department[]>((resolve, reject) => {
      return this.http
        .get<Department[]>(this.apiHost + '/Department/GetActive')
        .toPromise()
        .then(
          (data) => {
            resolve(data['dataResult']);
          }, 
          (msg) => {
            if (this.logService != null)
              this.logService.addToLog('Admin: Get Active Departments', msg);
            reject(msg);
          }
        );
    });
    return promise;
  }

  getAllDepartments(): Promise<Department[]> {
    let promise = new Promise<Department[]>((resolve, reject) => {
      return this.http
        .get<Department[]>(this.apiHost + '/Department/getall')
        .toPromise()
        .then(
          (data) => {
            resolve(data['dataResult']);
          },
          (msg) => {
            if (this.logService != null)
              this.logService.addToLog('Admin: Get All Departments', msg);
            reject(msg);
          }
        );
    });
    return promise;
  }

  addDepartment(Department: Department): Promise<Department> {
    let promise = new Promise<Department>((resolve, reject) => {
      return this.http
        .post<Department>(this.apiHost + '/Department/add', Department)
        .toPromise()
        .then(
          (data) => {
            resolve(data);
          },
          (msg) => {
            if (this.logService != null)
              this.logService.addToLog('Admin: Add Department', msg);
            reject(msg);
          }
        );
    });
    return promise;
  }

  saveDepartment(Department: Department): Promise<Department> {
    let promise = new Promise<Department>((resolve, reject) => {
      return this.http
        .post<Department>(this.apiHost + '/Department/update', Department)
        .toPromise()
        .then(
          (data) => {
            resolve(data);
          },
          (msg) => {
            if (this.logService != null)
              this.logService.addToLog('Admin: Update Department', msg);
            reject(msg);
          }
        );
    });
    return promise;
  }

  deleteDepartment(DepartmentId: number): Promise<Department> {
    let promise = new Promise<Department>((resolve, reject) => {
      return this.http
        .post<Department>(
          this.apiHost +
            '/Department/delete?DepartmentId=' +
            DepartmentId.toString() +
            '&ShowHide=false',
          null
        )
        .toPromise()
        .then(
          (data) => {
            resolve(data);
          },
          (msg) => {
            if (this.logService != null)
              this.logService.addToLog('Admin: Delete Department', msg);
            reject(msg);
          }
        );
    });
    return promise;
  }

  undeleteDepartment(DepartmentId: number): Promise<Department> {
    let promise = new Promise<Department>((resolve, reject) => {
      return this.http
        .post<Department>(
          this.apiHost +
            '/Department/delete?DepartmentId=' +
            DepartmentId.toString() +
            '&ShowHide=true',
          null
        )
        .toPromise()
        .then(
          (data) => {
            resolve(data);
          },
          (msg) => {
            if (this.logService != null)
              this.logService.addToLog('Admin: Delete Department', msg);
            reject(msg);
          }
        );
    });
    return promise;
  }

  promoteDepartment(departmentId: number): Promise<any> {
    let promise = new Promise<any>((resolve, reject) => {
      return this.http
        .post<any>(
          this.apiHost +
            '/Department/PromoteDepartment?DepartmentId=' +
            departmentId.toString(),
          null
        )
        .toPromise()
        .then(  
          (data) => {
            if (data.statusCode == 200) resolve(data);
            else reject(data);
          },
          (msg) => {
            if (this.logService != null)
              this.logService.addToLog('Admin: Promote Department', msg);
            reject(msg);
          }
        );
    });
    return promise;
  }

  demoteDepartment(departmentId: number): Promise<any> {
    let promise = new Promise<any>((resolve, reject) => {
      return this.http
        .post<any>(
          this.apiHost +
            '/Department/DemoteDepartment?DepartmentId=' +
            departmentId.toString(),
          null
        )
        .toPromise()
        .then(
          (data) => {
            if (data.statusCode == 200) resolve(data);
            else reject(data);
          },
          (msg) => {
            if (this.logService != null)
              this.logService.addToLog('Admin: Demote Department', msg);
            reject(msg);
          }
        );
    });
    return promise;
  }

  confirmPromoteDepartment(departmentId: number): Promise<any> {
    let promise = new Promise<any>((resolve, reject) => {
      return this.http
        .post<any>(
          this.apiHost +
            '/Department/ConfirmPromoteDepartment?DepartmentId=' +
            departmentId.toString(),
          null
        )
        .toPromise()
        .then(
          (data) => {
            if (data.statusCode == 200) resolve(data);
            else reject(data);
          },
          (msg) => {
            if (this.logService != null)
              this.logService.addToLog(
                'Admin: Confirm Promote Department',
                msg
              );
            reject(msg);
          }
        );
    });
    return promise;
  }

  confirmDemoteDepartment(departmentId: number): Promise<any> {
    let promise = new Promise<any>((resolve, reject) => {
      return this.http
        .post<any>(
          this.apiHost +
            '/Department/ConfirmDemoteDepartment?DepartmentId=' +
            departmentId.toString(),
          null
        )
        .toPromise()
        .then(
          (data) => {
            if (data.statusCode == 200) resolve(data);
            else reject(data);
          },
          (msg) => {
            if (this.logService != null)
              this.logService.addToLog('Admin: Confirm Demote Department', msg);
            reject(msg);
          }
        );
    });
    return promise;
  }
}
